import { useMemo } from 'react';
import moment from 'moment';
import queryString from 'query-string';
import slugify from 'slugify';
import { firmWaterfallUrl, waterfallUrl } from 'common/config/urls';
import type { TertiaryNavigationLinkItem } from 'context/FirmBaseLayoutContext';
import type { WaterfallView } from 'pages/Waterfall/api/waterfallViews';
import { MOMENT_DEFAULT_DATE_FORMAT } from 'utilities';
import {
  WATERFALL_QUERY_PARAM_CAP_TABLE_VERSION,
  WATERFALL_QUERY_PARAM_EXIT_DATE,
  WATERFALL_QUERY_PARAM_EXIT_ENTERPRISE_VALUE,
  WATERFALL_QUERY_PARAM_EXIT_EQUITY_VALUE,
  WATERFALL_QUERY_PARAM_LESS_DEBT,
  WATERFALL_QUERY_PARAM_PLUS_CASH,
} from '../../constants';
import type { WaterfallUrlState } from '../useWaterfallUrlState/useWaterfallUrlState.props';

const DEFAULT_VIEW_VALUE = '__default__';

interface UseWaterfallViewNavItemsParams {
  views: WaterfallView[];
  urlState: WaterfallUrlState;
  isFirmPage: boolean;
  firmSlug: string;
  companySlug: string;
  companyList?: Array<{ company_id: number; company_name?: string }> | null;
  onSelectDefault?: () => void;
  onSelectView?: (viewId: number) => void;
}

interface WaterfallViewNavItemsResult {
  items: TertiaryNavigationLinkItem[];
  activeValue: string;
  activeViewId: number | null;
}

const nullishEqual = (a: string | number | null | undefined, b: string | number | null | undefined) => {
  const aEmpty = a == null || a === '';
  const bEmpty = b == null || b === '';
  if (aEmpty && bEmpty) return true;
  if (aEmpty !== bEmpty) return false;
  return String(a) === String(b);
};

const numericEqual = (urlVal: number | undefined, viewVal: string | null) => {
  const urlEmpty = urlVal == null;
  const viewEmpty = viewVal == null;
  if (urlEmpty && viewEmpty) return true;
  if (urlEmpty !== viewEmpty) return false;
  return Math.abs(Number(urlVal) - Number(viewVal)) < 0.001;
};

// exitDateFromUrl is in MM/DD/YYYY; view.exit_date from backend is ISO YYYY-MM-DD. Normalize to ISO.
const toIsoDate = (d: string | null | undefined): string | null | undefined => {
  if (d == null) return d;
  const m = moment(d, MOMENT_DEFAULT_DATE_FORMAT, true);
  return m.isValid() ? m.format('YYYY-MM-DD') : d;
};

const viewMatchesUrl = (view: WaterfallView, urlState: WaterfallUrlState, isFirmPage: boolean): boolean => {
  if (isFirmPage && !nullishEqual(urlState.companyIdFromUrl, view.company)) return false;
  if (!nullishEqual(urlState.measurementDateValueFromUrl, view.measurement_date_value)) return false;
  if (!nullishEqual(urlState.captableIdFromUrl, view.cap_table)) return false;
  if (!nullishEqual(toIsoDate(urlState.exitDateFromUrl), view.exit_date)) return false;
  if (!numericEqual(urlState.equityValueFromUrl, view.exit_equity_value)) return false;
  if (!numericEqual(urlState.enterpriseValueFromUrl, view.exit_enterprise_value)) return false;
  if (!numericEqual(urlState.cashOverrideFromUrl, view.plus_cash)) return false;
  if (!numericEqual(urlState.debtOverrideFromUrl, view.less_debt)) return false;
  return true;
};

const buildViewUrl = (
  view: WaterfallView,
  isFirmPage: boolean,
  firmSlug: string,
  companySlug: string,
  companyList?: Array<{ company_id: number; company_name?: string }> | null
): string => {
  const basePath = isFirmPage
    ? firmWaterfallUrl(firmSlug)
    : waterfallUrl(firmSlug, companySlug);

  const params: Record<string, string> = {};

  if (view.measurement_date_value) {
    params.date = view.measurement_date_value;
  }

  if (view.cap_table != null) {
    params[WATERFALL_QUERY_PARAM_CAP_TABLE_VERSION] = `captable-${view.cap_table}`;
  }

  if (view.exit_date) {
    params[WATERFALL_QUERY_PARAM_EXIT_DATE] = view.exit_date;
  }

  if (view.exit_equity_value != null) {
    params[WATERFALL_QUERY_PARAM_EXIT_EQUITY_VALUE] = view.exit_equity_value;
  }

  if (view.exit_enterprise_value != null) {
    params[WATERFALL_QUERY_PARAM_EXIT_ENTERPRISE_VALUE] = view.exit_enterprise_value;
  }

  if (view.plus_cash != null) {
    params[WATERFALL_QUERY_PARAM_PLUS_CASH] = view.plus_cash;
  }

  if (view.less_debt != null) {
    params[WATERFALL_QUERY_PARAM_LESS_DEBT] = view.less_debt;
  }

  if (isFirmPage) {
    const companyName = companyList?.find(c => c.company_id === view.company)?.company_name ?? 'company';
    const slug = `${slugify(companyName, { lower: true })}-${view.company}`;
    params.company = slug;
  }

  const qs = queryString.stringify(params);
  return qs ? `${basePath}?${qs}` : basePath;
};

const useWaterfallViewNavItems = ({
  views,
  urlState,
  isFirmPage,
  firmSlug,
  companySlug,
  companyList,
  onSelectDefault,
  onSelectView,
}: UseWaterfallViewNavItemsParams): WaterfallViewNavItemsResult => {
  const basePath = isFirmPage ? firmWaterfallUrl(firmSlug) : waterfallUrl(firmSlug, companySlug);

  const items = useMemo<TertiaryNavigationLinkItem[]>(() => {
    const defaultItem: TertiaryNavigationLinkItem = {
      value: DEFAULT_VIEW_VALUE,
      label: 'Current',
      // onSelect resets URL-default init flags; to performs the actual navigation.
      // The layout calls onSelect first, then navigate(to), so defaults re-fire on the new URL.
      onSelect: onSelectDefault,
      to: basePath,
    };

    const viewItems: TertiaryNavigationLinkItem[] = views.map(view => ({
      value: String(view.id),
      label: view.name,
      to: buildViewUrl(view, isFirmPage, firmSlug, companySlug, companyList),
      showMenu: true,
      onSelect: onSelectView ? () => onSelectView(view.id) : undefined,
    }));

    return [defaultItem, ...viewItems];
  }, [views, isFirmPage, firmSlug, companySlug, companyList, basePath, onSelectView, onSelectDefault]);

  const activeViewId = useMemo<number | null>(() => {
    const match = views.find(v => viewMatchesUrl(v, urlState, isFirmPage));
    return match?.id ?? null;
  }, [views, urlState, isFirmPage]);

  const activeValue = activeViewId != null ? String(activeViewId) : DEFAULT_VIEW_VALUE;

  return { items, activeValue, activeViewId };
};

export default useWaterfallViewNavItems;
export { DEFAULT_VIEW_VALUE };
export type { UseWaterfallViewNavItemsParams, WaterfallViewNavItemsResult };
